/* eslint-disable react/require-default-props */
// Replacing apps/editor/components/PageSidebar/index.tsx
import { ArrowLeft, CaretDoubleLeft } from '@phosphor-icons/react';
import { History } from '@styled-icons/material';
import { Dashboard, Info, WebAsset } from '@styled-icons/material-outlined';
import { useDreamEditorRef } from '@typedream/dream-editor';
import router from 'next/router';
import { PropsWithChildren, useContext, useEffect, useState } from 'react';

import BetaVersion from '@/components/BetaText/BetaVersion';
import CollectionTutorialPopup from '@/components/CollectionTutorialPopup';
import { CollectionTutorialProvider } from '@/components/CollectionTutorialPopup/context';
import HelpPopup from '@/components/HelpPopup';
import { HelpPopupDispatchContext, HelpPopupProvider } from '@/components/HelpPopup/context';
import { TemplatesTabContent } from '@/components/PageSidebar/components';
import SaveDraftButton from '@/components/SaveDraftButton';
import SaveTemplatePopup from '@/components/SaveTemplatePopup';
import { SavePageTemplatePopupProvider } from '@/components/SaveTemplatePopup/contextProvider';
import { TabContent, TabList, TabRoot, TabTrigger } from '@/components/TabsSolid';
import TutorialPopup from '@/components/TutorialPopup';
import { TutorialProvider } from '@/components/TutorialPopup/context';
import UnsavedChangesButton from '@/components/UnsavedChangesButton';
import UpgradeBanner from '@/components/UpgradeBanner';
import { VersionHistoryPopup } from '@/components/VersionHistoryPopUp';
import {
  VersionHistoryDispatchContext,
  VersionHistoryProvider,
} from '@/components/VersionHistoryPopUp/context';
import {
  MenuBlogs,
  MenuCollapsible,
  MenuForms,
  MenuLinkInBio,
  MenuMails,
  MenuPages,
  MenuProducts,
} from '@/components/dashboard/Menu';
import { MenuAddWorkspace } from '@/components/dashboard/Menu/AddWorkspace';
import {
  DashboardSidenavContext,
  DashboardSidenavContextProvider,
} from '@/components/dashboard/context';
import { useSpaceAndPageIDFromRouter } from '@/data/space/hooks';
import CollectionSetupFlow from '@/sharedComponents/CollectionSetupFlow';
import {
  DEFAULT_MIN_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_ZINDEX,
} from '@/sharedComponents/TextEditor/constants';
import {
  SidebarContext,
  SidebarDispatchContext,
} from '@/sharedComponents/TextEditor/context/SidebarContextProvider';
import { useTPlateEditorRef } from '@/sharedComponents/TextEditor/plate';
import { useFeatureFlag, useListSpacesQuery } from '@/store';
import flags from '@/store/flags';
import isGoogleChromeDesktop from '@/utils/isGoogleChromeDesktop';
import { BookOpen, Layout } from '@styled-icons/boxicons-regular';
import classNames from 'classnames';
import { SidebarItem } from '../SidebarItem/SidebarItem';
import SpaceTitle from './SpaceTitle';

type EditorSidebarProps = PropsWithChildren<{
  saveDraftButton?: boolean;
  versionHistoryButton?: boolean;
  legacy?: boolean;
  tabDisabled?: boolean;
  id?: string;
}>;

function EditorSidebarWrapper(props: PropsWithChildren<object>) {
  return (
    <HelpPopupProvider>
      <TutorialProvider>
        <CollectionTutorialProvider>
          <SavePageTemplatePopupProvider>
            <DashboardSidenavContextProvider>
              <VersionHistoryProvider>{props.children}</VersionHistoryProvider>
            </DashboardSidenavContextProvider>
          </SavePageTemplatePopupProvider>
        </CollectionTutorialProvider>
      </TutorialProvider>
    </HelpPopupProvider>
  );
}

function BaseEditorSidebar({
  saveDraftButton = true,
  versionHistoryButton = false,
  legacy = false,
  tabDisabled = false,
  id,
  children,
}: EditorSidebarProps) {
  const { slug } = router.query;
  // TODO: Use the implementatino from Sidebar
  const sidebarOpen = useContext(SidebarContext);
  const setsidebarOpen = useContext(SidebarDispatchContext);
  const [sidebarZIndex, setsidebarZIndex] = useState(DEFAULT_SIDEBAR_ZINDEX);
  const [tab, settab] = useState('pages');
  const emailFlag = useFeatureFlag(flags.EmailMarketing);
  const { openOptions } = useContext(DashboardSidenavContext);

  // TODO: Add show chrome warn
  // Why is this needed here?
  const [isShowChromeWarn, setIsShowChromeWarn] = useState(false);
  const legacyEditor = useTPlateEditorRef(id);
  const dreamEditor = useDreamEditorRef(id);

  const [spaceID] = useSpaceAndPageIDFromRouter();
  const editor = legacy ? legacyEditor : dreamEditor;

  const sethelpPopupOpen = useContext(HelpPopupDispatchContext);
  const { data: spaces } = useListSpacesQuery();
  // Automatically hide sidebar on small screensize
  useEffect(() => {
    if (window && window.innerWidth) {
      setsidebarOpen(window.innerWidth >= DEFAULT_SIDEBAR_WIDTH + DEFAULT_MIN_WIDTH);
    }
    setIsShowChromeWarn(!isGoogleChromeDesktop());
  }, []);

  // Hack for legacy editor to prevent video elements overflowing without
  // changing the positioning of video.
  useEffect(() => {
    if (sidebarOpen) {
      setsidebarZIndex(sidebarZIndex + 1);
    } else {
      setsidebarZIndex(sidebarZIndex - 1);
    }
  }, [sidebarOpen]);

  const setVersionHistoryPopupOpen = useContext(VersionHistoryDispatchContext);

  return (
    <>
      {/* Might not needed */}
      {children}
      {/* Fixed drawer, used for pushing the Dream Editor to the right */}
      <div
        css={{
          width: `${sidebarOpen ? DEFAULT_SIDEBAR_WIDTH : 0}px`,
        }}
      />
      <div
        className={classNames(
          'fixed left-0 top-0 bottom-0 flex flex-col justify-between overflow-hidden transition-all duration-200 ease-out',
          openOptions ? '!bg-background-secondary !z-20' : '!z-[1]'
        )}
        style={{
          backgroundColor: 'var(--side-background)',
          borderRight: '1px solid var(--border-modal)',
          width: DEFAULT_SIDEBAR_WIDTH,
          left: sidebarOpen ? 0 : -DEFAULT_SIDEBAR_WIDTH - 10,
        }}
      >
        <HelpPopup />
        <TutorialPopup />
        <CollectionTutorialPopup />
        <CollectionSetupFlow connectElement={false} allowConnectIfConnected={false} />
        {versionHistoryButton && <VersionHistoryPopup />}
        <SaveTemplatePopup />
        <div className="flex justify-between items-center p-2 gap-2">
          <button
            type="button"
            className="flex items-center justify-center h-[38px] w-[38px] bg-transparent border-0 focus:outline-0 p-2 text-secondary rounded-md hover:bg-background-highlight"
            onClick={() => router.push(`/dashboard/${spaceID}`)}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <SpaceTitle />
          </div>
          <button
            type="button"
            className="flex items-center justify-center h-[38px] w-[38px] bg-transparent border-0 focus:outline-0 p-2 text-secondary rounded-md hover:bg-background-highlight"
            onClick={() => setsidebarOpen(false)}
          >
            <CaretDoubleLeft size={20} />
          </button>
        </div>
        <TabRoot
          value={tab}
          onValueChange={settab}
          className="flex-1 h-full overflow-hidden p-[18px] !gap-0"
        >
          <TabList className="overflow-hidden h-[44px] mb-2.5">
            <TabTrigger
              className="flex items-center justify-center py-1.5 !px-2.5 gap-1 !text-[13px]"
              value="pages"
              disabled={tabDisabled || !id}
            >
              <WebAsset size={18} />
              Pages
            </TabTrigger>
            <TabTrigger
              className="flex items-center justify-center py-1.5 !px-2.5 gap-1 !text-[13px]"
              value="templates"
              disabled={tabDisabled || !id}
            >
              <Dashboard size={18} />
              Templates
            </TabTrigger>
          </TabList>
          <div className="flex-1 h-full overflow-hidden mx-[-18px]">
            {tab === 'pages' && (
              <TabContent value="pages" className="flex flex-col gap-6 h-full">
                <div className="px-[18px]">
                  {versionHistoryButton && (
                    <SidebarItem
                      onClick={() => setVersionHistoryPopupOpen(true)}
                      left={<History size={15} />}
                      center="Version History"
                    />
                  )}
                </div>
                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-x-hidden overflow-y-auto">
                    <div
                      className="flex flex-col gap-1 px-[18px]"
                      style={{
                        width: DEFAULT_SIDEBAR_WIDTH,
                      }}
                    >
                      <MenuCollapsible
                        heading="Pages"
                        suffix={<MenuAddWorkspace menu="pages" />}
                        defaultOpen
                      >
                        <MenuPages enableEmpty enableDelete />
                      </MenuCollapsible>
                      <MenuCollapsible heading="Forms" suffix={<MenuAddWorkspace menu="forms" />}>
                        <MenuForms />
                      </MenuCollapsible>
                      <MenuCollapsible heading="Blogs" suffix={<MenuAddWorkspace menu="blogs" />}>
                        <MenuBlogs />
                      </MenuCollapsible>
                      <MenuCollapsible
                        heading="Link in Bio"
                        suffix={<MenuAddWorkspace menu="link_in_bio" />}
                      >
                        <MenuLinkInBio />
                      </MenuCollapsible>
                      <MenuCollapsible
                        heading="Products"
                        suffix={<MenuAddWorkspace menu="products" />}
                      >
                        <MenuProducts />
                      </MenuCollapsible>
                      {emailFlag && (
                        <MenuCollapsible
                          heading="Emails"
                          suffix={<MenuAddWorkspace menu="emails" />}
                        >
                          <MenuMails />
                        </MenuCollapsible>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-[18px]">
                  <SidebarItem
                    onClick={() => router.push(`/s/${slug}/${spaceID}/tsc`)}
                    left={<Layout size={15} />}
                    center="Synced Containers"
                  />
                </div>
              </TabContent>
            )}
            {tab === 'templates' && (
              <TabContent value="templates" className="flex flex-col gap-6 h-full">
                <div className="px-[18px] h-full">
                  <TemplatesTabContent editor={editor} resetSidebarTab={() => settab('pages')} />
                </div>
              </TabContent>
            )}
          </div>
          <div className="mx-[-18px] mb-[-18px]">
            <div className="px-[18px]">
              <SidebarItem
                onClick={() => sethelpPopupOpen(true)}
                left={<BookOpen size={15} />}
                center="Help & Resources"
              />
              <SidebarItem disabled left={<Info size={15} />} center={<BetaVersion />} />
            </div>
            <UnsavedChangesButton />
            {saveDraftButton && <SaveDraftButton />}
            {spaces?.[spaceID as string] && !spaces?.[spaceID as string].custom_domain && (
              <UpgradeBanner />
            )}
          </div>
        </TabRoot>
      </div>
    </>
  );
}

export function EditorSidebar(props: EditorSidebarProps) {
  return (
    <EditorSidebarWrapper>
      <BaseEditorSidebar {...props} />
    </EditorSidebarWrapper>
  );
}
